// CMP-EmptyState, CMP-ErrorState et états transverses ETAT-01 à ETAT-08 (maquettes 12bis-11, textes exacts).
import { CircleX, CloudOff, FileQuestion, Inbox, Lock, SearchX, TimerOff, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { Button } from './Button';
import { Skeleton } from './Skeleton';

interface StateLayoutProps {
  icon: LucideIcon;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  role?: 'alert' | 'status';
  tone?: 'neutral' | 'error' | 'warning';
  className?: string;
  stateId?: string;
}

function StateLayout({ icon: Icon, title, body, action, role, tone = 'neutral', className, stateId }: StateLayoutProps) {
  return (
    <div role={role} data-state-id={stateId} className={cn('flex flex-col items-center justify-center gap-3 px-6 py-12 text-center', className)}>
      <span
        className={cn(
          'flex size-16 items-center justify-center rounded-full',
          tone === 'error' ? 'bg-error-bg text-error' : tone === 'warning' ? 'bg-warning-bg text-warning' : 'bg-surface-2 text-text-2',
        )}
      >
        <Icon aria-hidden="true" className="size-8" />
      </span>
      <h2 className="max-w-xl text-h3 font-bold">{title}</h2>
      {body && <p className="max-w-xl text-body text-text-2">{body}</p>}
      {action && <div className="mt-2 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  );
}

/** ETAT-01 — chargement : squelette de la page (aria-busy). */
export function LoadingState({ rows = 6, label }: { rows?: number; label?: string }) {
  const { t } = useTranslation('states');
  return (
    <div role="status" aria-busy="true" aria-label={label ?? t('loading')} data-state-id="ETAT-01" className="flex flex-col gap-3">
      <Skeleton className="h-9 w-72" />
      <Skeleton className="h-5 w-96 max-w-full" />
      <div className="mt-2 flex flex-col gap-2 rounded-lg border border-border bg-surface p-4">
        {Array.from({ length: rows }, (_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    </div>
  );
}

/** ETAT-02 — liste vide (première utilisation), texte contextualisé par la page. */
export function EmptyState({ title, body, action, icon = Inbox }: { title?: ReactNode; body?: ReactNode; action?: ReactNode; icon?: LucideIcon }) {
  const { t } = useTranslation('states');
  return <StateLayout stateId="ETAT-02" icon={icon} title={title ?? t('empty.title')} body={body ?? t('empty.body')} action={action} role="status" />;
}

/** ETAT-03 — aucun résultat (filtres) : réinitialiser. */
export function NoResultsState({ onReset, body }: { onReset?: () => void; body?: ReactNode }) {
  const { t } = useTranslation('states');
  const { t: tc } = useTranslation();
  return (
    <StateLayout
      stateId="ETAT-03"
      icon={SearchX}
      title={t('no_results.title')}
      body={body ?? t('no_results.body')}
      role="status"
      action={onReset && <Button onClick={onReset}>{tc('actions.reset_filters')}</Button>}
    />
  );
}

/** ETAT-04 — erreur serveur : cause, « Réessayer », référence support. */
export function ErrorState({ message, reference, onRetry }: { message?: string; reference?: string; onRetry?: () => void }) {
  const { t } = useTranslation('states');
  const { t: tc } = useTranslation();
  return (
    <StateLayout
      stateId="ETAT-04"
      icon={CircleX}
      tone="error"
      role="alert"
      title={t('server_error.title')}
      body={
        <>
          {message ?? t('server_error.body')}
          {reference && <span className="mt-1 block font-mono text-small">{t('server_error.code', { reference })}</span>}
        </>
      }
      action={onRetry && <Button variant="primary" onClick={onRetry}>{tc('actions.retry')}</Button>}
    />
  );
}

/** ETAT-05 — hors ligne avec données datées (bandeau au-dessus du contenu en cache). */
export function OfflineBanner({ date }: { date: string }) {
  const { t } = useTranslation('states');
  return (
    <div role="status" data-state-id="ETAT-05" className="flex items-center gap-2 rounded-md bg-warning-bg px-3 py-2 text-small text-warning">
      <CloudOff aria-hidden="true" className="size-4 shrink-0" />
      {t('offline.body', { date })}
    </div>
  );
}

/**
 * ETAT-06 — accès refusé (réponse 403) : jamais de plantage. `body` et `homeLabel` : texte et retour propres à la page
 * (CAP-P02 : « Ce contenu est réservé à l'équipe du projet. », retour à la bibliothèque).
 */
export function ForbiddenState({ role, onHome, body, homeLabel }: { role?: string; onHome?: () => void; body?: ReactNode; homeLabel?: string }) {
  const { t } = useTranslation('states');
  const { t: tc } = useTranslation();
  return (
    <StateLayout
      stateId="ETAT-06"
      icon={Lock}
      tone="warning"
      role="alert"
      title={t('forbidden.title')}
      body={body ?? (role ? t('forbidden.body', { role }) : t('forbidden.body_generic'))}
      action={onHome && <Button onClick={onHome}>{homeLabel ?? tc('actions.back_home')}</Button>}
    />
  );
}

/** ETAT-07 — page introuvable. */
export function NotFoundState({ onHome }: { onHome?: () => void }) {
  const { t } = useTranslation('states');
  const { t: tc } = useTranslation();
  return (
    <StateLayout
      stateId="ETAT-07"
      icon={FileQuestion}
      role="alert"
      title={t('not_found.title')}
      body={t('not_found.body')}
      action={onHome && <Button onClick={onHome}>{tc('actions.back_home')}</Button>}
    />
  );
}

/** ETAT-08 — session expirée (superposition bloquante de l'AppShell). */
export function SessionExpiredState({ onSignIn }: { onSignIn: () => void }) {
  const { t } = useTranslation('states');
  const { t: tc } = useTranslation();
  return (
    <StateLayout
      stateId="ETAT-08"
      icon={TimerOff}
      role="alert"
      title={t('session_expired.title')}
      body={t('session_expired.body')}
      action={<Button variant="primary" onClick={onSignIn}>{tc('actions.sign_in_again')}</Button>}
    />
  );
}
